import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Route, Routes, useNavigate, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import errorImage from './error.png';
import Person from './person';
import Sidebar from './Sidebar';
import AppHeader from './AppHeader';
import UserForm from './UserForm';
import IzinSayfasi from './IzinSayfasi';
import UserManagement from './UserManagement';
import Login from './login';
import HomePage from './homepage';
import Kurumbilgi from './Kurumbilgi';
import Hakedis from './hakedis';
import IzinOnay from './IzinOnay';
import TarihSec from './TarihSec';
import TatilVeIzin from './TatilVeIzin';
import Raporlar from './Raporlar';

const API = 'https://api.idekod.com.tr';
const DESKTOP_QUERY = '(min-width: 1024px)';

/* Masaüstü / mobil ayrımı: sidebar masaüstünde sabit, mobilde çekmece olarak çalışır */
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    setIsDesktop(mq.matches);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return isDesktop;
};

/* Kurum + kullanıcı bilgisi tek yerden çekilip header ve sidebar'a dağıtılır */
const useOrgInfo = (enabled) => {
  const [info, setInfo] = useState({ rname: '', kurumAd: '', logo: '' });

  useEffect(() => {
    if (!enabled) return undefined;
    const username = localStorage.getItem('username');
    const company = localStorage.getItem('company');
    let alive = true;

    if (username) {
      fetch(`${API}/api/personel/${username}`)
        .then((r) => r.json())
        .then((d) => alive && setInfo((s) => ({ ...s, rname: d?.RNAME || '' })))
        .catch((e) => console.error('RNAME alınırken hata oluştu:', e));
    }

    if (company) {
      fetch(`${API}/api/kurumbilgi/${company}`)
        .then((r) => r.json())
        .then((d) => alive && setInfo((s) => ({ ...s, kurumAd: d?.kurumad || '', logo: d?.logourl || '' })))
        .catch((e) => console.error('Kurum bilgisi alınırken hata oluştu:', e));
    }

    return () => { alive = false; };
  }, [enabled]);

  return info;
};

const NotFoundPage = () => {
  const navigate = useNavigate();

  return (
    <div className="not-found">
      <img src={errorImage} alt="" />
      <h1 className="not-found__title">Sayfa bulunamadı</h1>
      <p>Aradığınız sayfaya ulaşılamadı. Lütfen tekrar giriş yapınız.</p>
      <button className="btn btn--primary" onClick={() => navigate('/login')}>
        Giriş Sayfasına Dön
      </button>
    </div>
  );
};

/* Chrome (header + sidebar + footer) gizlenecek rotalar */
const BARE_ROUTES = ['/', '/login', '/404'];

const MainContent = () => {
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const [collapsed, setCollapsed] = useState(false);   // masaüstü: daraltılmış menü
  const [mobileOpen, setMobileOpen] = useState(false); // mobil: çekmece açık mı

  const isBare = BARE_ROUTES.includes(location.pathname);
  const org = useOrgInfo(!isBare);

  // Rota değişince mobil çekmece kapansın
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Masaüstüne geçince çekmece durumu sıfırlansın
  useEffect(() => { if (isDesktop) setMobileOpen(false); }, [isDesktop]);

  // Çekmece açıkken arka plan kaymasın + ESC ile kapansın
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen]);

  const toggleSidebar = () =>
    isDesktop ? setCollapsed((v) => !v) : setMobileOpen((v) => !v);

  const routes = (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/company" element={<Kurumbilgi />} />
      <Route path="/pjoin" element={<UserForm />} />
      <Route path="/pedit" element={<UserManagement />} />
      <Route path="/person" element={<Person />} />
      <Route path="/permission" element={<IzinSayfasi />} />
      <Route path="/editpermission" element={<IzinOnay />} />
      <Route path="/hakedis" element={<Hakedis />} />
      <Route path="/reports" element={<Raporlar />} />
      <Route path="/help" element={<TarihSec />} />
      <Route path="/TatilVeIzin" element={<TatilVeIzin />} />
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );

  if (isBare) {
    return <div className="app-shell app-shell--bare">{routes}</div>;
  }

  const sidebarClasses = ['app-sidebar'];
  if (isDesktop && collapsed) sidebarClasses.push('app-sidebar--collapsed');
  if (!isDesktop && mobileOpen) sidebarClasses.push('app-sidebar--open');

  return (
    <div className="app-shell">
      <AppHeader
        kurumAd={org.kurumAd}
        logo={org.logo}
        rname={org.rname}
        onToggleSidebar={toggleSidebar}
        menuExpanded={isDesktop ? !collapsed : mobileOpen}
        showClose={!isDesktop && mobileOpen}
      />

      <div className="app-body">
        <aside className={sidebarClasses.join(' ')} aria-label="Ana menü">
          <Sidebar collapsed={isDesktop && collapsed} />
        </aside>

        <div
          className={`app-backdrop ${mobileOpen ? 'app-backdrop--visible' : ''}`}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />

        <div className={`app-main ${isDesktop && collapsed ? 'app-main--collapsed' : ''}`}>
          <main className="app-content">{routes}</main>
          <footer className="app-footer">
            <p>&copy; {new Date().getFullYear()} İDEKOD &middot; Personel Takip Sistemi</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

const App = () => (
  <Router>
    <MainContent />
  </Router>
);

export default App;
